<template>
  <transition name="notification-fade">
    <div v-if="visible" class="login-notification" :class="[`is-${notificationType}`, { 'is-dark': isDark }]">
      <div class="notification-content">
        <!-- Icon -->
        <div class="notification-icon">
          <svg v-if="notificationType === 'success'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          <svg v-else-if="notificationType === 'error'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          <svg v-else-if="notificationType === 'danger' || notificationType === 'locked'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>

        <!-- Message Content -->
        <div class="notification-body">
          <!-- Failed Attempts Warning -->
          <div v-if="showFailedAttempts" class="notification-section">
            <p class="notification-title">{{ $t('authentication.failedAttempts.title') }}</p>
            <p class="notification-message">
              {{ $t('authentication.failedAttempts.message', { 
                failed: error.loginAttempts.failed, 
                remaining: error.loginAttempts.remaining 
              }) }}
            </p>
          </div>

          <!-- Account Locked Warning with Countdown -->
          <div v-else-if="showAccountLocked" class="notification-section">
            <p class="notification-title">{{ $t('authentication.accountLocked.title') }}</p>
            <p class="notification-message">
              {{ $t('authentication.accountLocked.message') }}
            </p>
            <div v-if="showCountdown" class="countdown-container">
              <p class="countdown-label">{{ $t('authentication.accountLocked.countdownLabel') }}</p>
              <div class="countdown-display">
                <span class="countdown-time">{{ formattedCountdown }}</span>
              </div>
            </div>
            <p v-else-if="lockoutExpired" class="unlock-message">
              {{ $t('authentication.accountLocked.unlockMessage') }}
            </p>
            <p v-else class="lockout-status">
              {{ $t('authentication.accountLocked.waitMessage') }}
            </p>
          </div>

          <!-- General Error Message -->
          <div v-else-if="showGeneralError" class="notification-section">
            <p class="notification-title">{{ $t('authentication.generalError.title') }}</p>
            <p class="notification-message">{{ generalErrorMessage }}</p>
          </div>

          <!-- Simple Message (backward compatibility) -->
          <div v-else class="notification-section">
            <p class="notification-message">{{ message }}</p>
          </div>
        </div>

        <!-- Close Button -->
        <button class="notification-close" @click="close" type="button">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
import { countdownTimerMixin } from '@/composables/useCountdownTimer'

/**
 * LoginNotification Component
 * 
 * Unified notification component for login-related messages including:
 * - Simple success/error/warning/info messages
 * - Failed login attempt tracking
 * - Account lockout with real-time countdown timer
 * - General error messages
 * 
 * Props:
 * - message: Simple message string (for backward compatibility)
 * - type: Notification type ('success', 'error', 'warning', 'info')
 * - error: Error object from authentication API (for advanced notifications)
 * - visible: Boolean to control visibility
 * - duration: Auto-close duration in milliseconds (0 to disable)
 * 
 * Events:
 * - unlock: Emitted when countdown reaches zero
 * - close: Emitted when notification should be closed
 */
export default {
  name: 'LoginNotification',
  mixins: [countdownTimerMixin],
  props: {
    message: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'info',
      validator: (value) => ['success', 'error', 'warning', 'info', 'danger', 'locked'].includes(value)
    },
    error: {
      type: Object,
      default: null
    },
    duration: {
      type: Number,
      default: 3000
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      remainingSeconds: 0,
      formattedCountdown: '0 seconds',
      lockoutExpired: false,
      validLockoutTime: false,
      autoCloseTimer: null
    }
  },
  computed: {
    isDark() {
      return this.$store?.state?.ui?.dark || false
    },
    
    /**
     * Determines the notification type based on error or type prop
     */
    notificationType() {
      if (this.showAccountLocked) {
        return 'danger'
      }
      if (this.showFailedAttempts) {
        return 'warning'
      }
      return this.type
    },

    /**
     * Whether to show failed attempts warning
     */
    showFailedAttempts() {
      return this.error &&
             this.error.code === 401 &&
             this.error.loginAttempts
    },

    /**
     * Whether to show account locked warning
     */
    showAccountLocked() {
      return this.error &&
             this.error.code === 423 &&
             this.error.accountLocked
    },

    /**
     * Whether to show general error message
     */
    showGeneralError() {
      return this.error &&
             !this.showFailedAttempts &&
             !this.showAccountLocked
    },

    /**
     * Whether to show countdown timer
     */
    showCountdown() {
      return this.showAccountLocked &&
             this.validLockoutTime &&
             this.remainingSeconds > 0 &&
             !this.lockoutExpired
    },

    /**
     * General error message
     */
    generalErrorMessage() {
      return this.error ? this.error.message : ''
    }
  },
  watch: {
    /**
     * Watch for changes in error prop to start/stop countdown
     */
    error: {
      handler(newError) {
        this.handleErrorChange(newError)
      }
    },

    /**
     * Watch for visibility changes
     */
    visible(newVal) {
      if (newVal) {
        // Don't auto-close for account locked notifications
        if (!this.showAccountLocked && this.duration > 0) {
          this.startAutoClose()
        }
      } else {
        this.stopCountdown()
        this.clearAutoCloseTimer()
      }
    }
  },
  mounted() {
    // Handle initial error after component is fully mounted
    if (this.error) {
      this.handleErrorChange(this.error)
    }
  },
  methods: {
    close() {
      this.stopCountdown()
      this.clearAutoCloseTimer()
      this.$emit('close')
    },

    startAutoClose() {
      this.clearAutoCloseTimer()
      this.autoCloseTimer = setTimeout(() => {
        this.close()
      }, this.duration)
    },

    clearAutoCloseTimer() {
      if (this.autoCloseTimer) {
        clearTimeout(this.autoCloseTimer)
        this.autoCloseTimer = null
      }
    },

    /**
     * Handles error prop changes
     * @param {Object} newError - New error object
     */
    handleErrorChange(newError) {
      this.stopCountdown()
      this.resetLockoutState()

      if (newError && newError.accountLocked && newError.accountLocked.lockedUntil) {
        this.initializeAccountLockout(newError.accountLocked.lockedUntil, newError.accountLocked.unlockTimeRemaining)
      }
    },

    /**
     * Resets all lockout-related state
     */
    resetLockoutState() {
      this.lockoutExpired = false
      this.validLockoutTime = false
      this.remainingSeconds = 0
      this.formattedCountdown = '0 seconds'
    },

    /**
     * Validates and initializes account lockout
     * @param {string|number} lockedUntil - Timestamp when account will be unlocked
     * @param {number} unlockTimeRemaining - Server-provided remaining time in seconds (optional)
     */
    initializeAccountLockout(lockedUntil, unlockTimeRemaining = null) {
      // Prefer server-provided remaining time for accuracy
      if (unlockTimeRemaining > 0) {
        this.startCountdownFromSeconds(unlockTimeRemaining)
        return
      }

      // Fallback to timestamp-based calculation
      const lockoutTimestamp = this.parseTimestamp(lockedUntil)
      if (!lockoutTimestamp) {
        this.validLockoutTime = false
        return
      }

      const remainingMs = this.calculateRemainingTime(lockoutTimestamp)
      if (remainingMs <= 0) {
        this.handleExpiredLockout()
        return
      }

      this.startCountdownFromSeconds(Math.floor(remainingMs / 1000))
    },

    /**
     * Calculates remaining time with UTC compensation
     * @param {number} lockoutTimestamp - Target timestamp in milliseconds
     * @returns {number} Remaining milliseconds (adjusted if needed)
     */
    calculateRemainingTime(lockoutTimestamp) {
      const now = Date.now()
      let remainingMs = lockoutTimestamp - now

      // Compensate for slight clock differences and network delays
      if (remainingMs < 0 && remainingMs > -60000) {
        remainingMs = Math.max(30000, 60000 + remainingMs)
      }

      return remainingMs
    },

    /**
     * Starts countdown from a given number of seconds
     * @param {number} seconds - Remaining seconds
     */
    startCountdownFromSeconds(seconds) {
      this.validLockoutTime = true
      this.remainingSeconds = seconds
      this.formattedCountdown = this.countdownTimer?.formatTime(seconds) || `${seconds} seconds`

      const targetTime = Date.now() + (seconds * 1000)
      this.startAccountLockoutCountdown(targetTime)
    },

    /**
     * Parses timestamp from various formats
     * @param {string|number} lockedUntil - Timestamp to parse
     * @returns {number|null} Valid timestamp in milliseconds or null if invalid
     */
    parseTimestamp(lockedUntil) {
      if (!lockedUntil) return null

      let timestamp
      if (typeof lockedUntil === 'string') {
        timestamp = new Date(lockedUntil).getTime()
      } else if (typeof lockedUntil === 'number') {
        // Convert seconds to milliseconds if needed
        timestamp = lockedUntil > 1e12 ? lockedUntil : lockedUntil * 1000
      } else {
        return null
      }

      // Validate timestamp is reasonable (within 24 hours)
      if (isNaN(timestamp) || timestamp <= 0 || timestamp > Date.now() + 86400000) {
        return null
      }

      return timestamp
    },

    /**
     * Handles lockout that has already expired
     */
    handleExpiredLockout() {
      this.validLockoutTime = false
      this.lockoutExpired = true
      this.remainingSeconds = 0
      this.formattedCountdown = '0 seconds'

      this.$emit('unlock')

      // Auto-close after a short delay
      setTimeout(() => {
        this.$emit('close')
      }, 3000)
    },

    /**
     * Starts the account lockout countdown timer
     * @param {number} targetTime - Target timestamp in milliseconds
     */
    startAccountLockoutCountdown(targetTime) {
      if (!this.countdownTimer) {
        this.$nextTick(() => {
          if (this.countdownTimer) {
            this.startAccountLockoutCountdown(targetTime)
          }
        })
        return
      }

      this.startCountdown(targetTime, {
        onTick: (seconds, formatted) => {
          this.remainingSeconds = seconds
          this.formattedCountdown = formatted
        },
        onComplete: () => {
          this.handleUnlock()
        }
      })
    },

    /**
     * Handles when countdown reaches zero
     */
    handleUnlock() {
      this.lockoutExpired = true
      this.remainingSeconds = 0
      this.formattedCountdown = '0 seconds'

      this.$emit('unlock')

      // Auto-close after showing unlock message
      setTimeout(() => {
        this.$emit('close')
      }, 3000)
    }
  },
  beforeDestroy() {
    this.clearAutoCloseTimer()
    if (this.countdownTimer) {
      this.countdownTimer.cleanup()
    }
  }
}
</script>

<style lang="scss" scoped>
.login-notification {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  min-width: 300px;
  max-width: 500px;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  background-color: #ffffff;
  border-left: 4px solid #3273dc;

  &.is-dark {
    background-color: #2b3040;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }

  &.is-success {
    border-left-color: #48c774;
    
    .notification-icon {
      color: #48c774;
    }
  }

  &.is-error {
    border-left-color: #f14668;
    
    .notification-icon {
      color: #f14668;
    }
  }

  &.is-warning {
    border-left-color: #ffdd57;
    
    .notification-icon {
      color: #ffdd57;
    }
  }

  &.is-danger {
    border-left-color: #f14668;
    
    .notification-icon {
      color: #f14668;
    }
  }

  &.is-info {
    border-left-color: #3273dc;
    
    .notification-icon {
      color: #3273dc;
    }
  }
}

.notification-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.notification-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}

.notification-body {
  flex: 1;
}

.notification-section {
  width: 100%;
}

.notification-title {
  font-weight: 600;
  margin-bottom: 0.25rem;
  font-size: 1rem;
  color: #363636;

  .is-dark & {
    color: #e8ebf1;
  }
}

.notification-message {
  font-size: 14px;
  line-height: 1.5;
  color: #363636;
  margin-bottom: 0.5rem;

  .is-dark & {
    color: #e8ebf1;
  }
}

.countdown-container {
  margin-top: 0.75rem;
  padding: 0.75rem;
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 4px;

  .is-dark & {
    background-color: rgba(31, 33, 40, 0.3);
    border: 1px solid rgba(221, 221, 223, 0.1);
  }
}

.countdown-label {
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #363636;

  .is-dark & {
    color: #e8ebf1;
  }
}

.countdown-display {
  text-align: center;
}

.countdown-time {
  font-size: 1.125rem;
  font-weight: 600;
  font-family: 'Courier New', monospace;
  padding: 0.5rem 1rem;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  display: inline-block;
  color: #363636;

  .is-dark & {
    background-color: rgba(31, 33, 40, 0.5);
    color: #dddddf;
    border: 1px solid rgba(221, 221, 223, 0.2);
  }
}

.unlock-message {
  margin-top: 0.75rem;
  padding: 0.5rem;
  background-color: rgba(72, 199, 116, 0.1);
  color: #155724;
  border-radius: 4px;
  font-weight: 500;

  .is-dark & {
    background-color: rgba(72, 199, 116, 0.1);
    color: #48c774;
    border: 1px solid rgba(72, 199, 116, 0.3);
  }
}

.lockout-status {
  margin-top: 0.75rem;
  padding: 0.5rem;
  background-color: rgba(255, 193, 7, 0.1);
  color: #856404;
  border-radius: 4px;
  font-weight: 500;
  font-style: italic;

  .is-dark & {
    background-color: rgba(255, 221, 87, 0.1);
    color: #ffdd57;
    border: 1px solid rgba(255, 221, 87, 0.3);
  }
}

.notification-close {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7a7a7a;
  transition: color 0.15s ease;

  &:hover {
    color: #363636;
  }

  .is-dark & {
    color: #9a9a9a;

    &:hover {
      color: #e8ebf1;
    }
  }
}

.notification-fade-enter-active,
.notification-fade-leave-active {
  transition: all 0.3s ease;
}

.notification-fade-enter {
  opacity: 0;
  transform: translateX(100px);
}

.notification-fade-leave-to {
  opacity: 0;
  transform: translateX(100px);
}
</style>
